export type EtsyShop = {
  shopId: string
  shopName: string
}

/** Result of GET /api/etsy/shops — includes worker error envelope when Etsy API fails. */
export type EtsyShopsResult = {
  shops: EtsyShop[]
  /** Present when the worker returned `{ error, message }` or the request failed. */
  fetchMessage?: string
  debug?: {
    brandId: string | null
    etsyUserId: number | string | null
    etsyUserLogin: string | null
    userShopsCount: number
    userShopsSample: Array<{ shopId: string | number | null; shopName: string | null }>
  }
}

export async function getEtsyShops(brandId?: string): Promise<EtsyShopsResult> {
  const params = new URLSearchParams()
  if (brandId) params.set('brandId', brandId)
  params.set('debug', '1')
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`/api/etsy/shops${qs}`)
  if (!res.ok) {
    return { shops: [], fetchMessage: `Could not load shops (HTTP ${res.status})` }
  }

  const data = await res.json()
  if (data?.error) {
    return {
      shops: [],
      fetchMessage: typeof data.message === 'string' ? data.message : String(data.error),
      debug: data?.debug,
    }
  }
  if (!Array.isArray(data?.shops)) {
    return { shops: [] }
  }

  const shops = (data.shops as unknown[]).map((s) => {
    const row = s as Record<string, unknown>
    return {
      shopId: row?.shopId !== undefined && row?.shopId !== null ? String(row.shopId) : '',
      shopName: row?.shopName !== undefined && row?.shopName !== null ? String(row.shopName) : '',
    }
  })
  let fetchMessage: string | undefined
  if (shops.length === 0 && data?.debug?.etsyUserId != null) {
    const dbg = data.debug
    fetchMessage = `Debug: Etsy user ${String(dbg.etsyUserId)}${
      dbg.etsyUserLogin ? ` (${String(dbg.etsyUserLogin)})` : ''
    } has ${String(dbg.userShopsCount)} shop(s) via Etsy API`
  }
  return { shops, fetchMessage, debug: data?.debug }
}

export async function selectEtsyShop(shopId: string, brandId?: string): Promise<void> {
  const qs = brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''
  const res = await fetch(`/api/etsy/shops/select${qs}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shopId }),
  })

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }

  const data = await res.json()
  if (data?.error) {
    throw new Error(data?.message || data?.error)
  }
}

